(() => {
  const isSizeInput = (input) =>
    input?.dataset?.optionName?.trim().toLowerCase() === 'size';
  const MESSAGE = 'Please choose a size.';

  const sizeInputs = (productInfo) =>
    [...productInfo.querySelectorAll('variant-selects input[data-option-name]')].filter(isSizeInput);

  const sizeSelects = (productInfo) =>
    [...productInfo.querySelectorAll('variant-selects select')].filter((select) =>
      [...select.options].some(isSizeInput));
  const initializedProducts = new WeakSet();
  const requiresSize = (productInfo) => sizeInputs(productInfo).length > 0 || sizeSelects(productInfo).length > 0;

  const hasSelectedSize = (productInfo) =>
    sizeInputs(productInfo).some((input) => input.checked) ||
    sizeSelects(productInfo).some((select) => Boolean(select.value));
  function hasCompleteSelection(productInfo) {
    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variantSelects) return false;

    const optionCount = variantSelects.querySelectorAll('fieldset, select').length;
    const selectedCount = variantSelects.querySelectorAll(
      'fieldset input:checked, select option[selected]'
    ).length;
    return optionCount > 0 && selectedCount === optionCount;
  }

  function selectedVariant(productInfo) {
    const json = productInfo.querySelector('variant-selects [data-selected-variant]')?.textContent;
    if (!json) return null;
    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  function selectedOptionValues(productInfo) {
    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variantSelects) return [];

    return [...variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown')]
      .map((group) => group.querySelector('input:checked, select option[selected]')?.value)
      .filter(Boolean);
  }

  function syncSelectedVariantId(productInfo) {
    if (!requiresSize(productInfo) || !hasSelectedSize(productInfo) || !hasCompleteSelection(productInfo)) return false;

    const variant = selectedVariant(productInfo);
    const optionValues = selectedOptionValues(productInfo);
    if (
      !variant?.id ||
      !Array.isArray(variant.options) ||
      optionValues.length !== variant.options.length ||
      !optionValues.every((value, index) => variant.options[index] === value)
    ) {
      return false;
    }

    productInfo
      .querySelectorAll('form[action*="/cart/add"] input[name="id"]')
      .forEach((input) => {
        const variantId = String(variant.id);
        if (input.value === variantId) return;
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    return true;
  }

  function scheduleVariantIdSync(productInfo, attempt = 0) {
    if (syncSelectedVariantId(productInfo) || attempt >= 12 || !hasSelectedSize(productInfo)) return;
    window.setTimeout(() => scheduleVariantIdSync(productInfo, attempt + 1), 150);
  }

  function setDynamicCheckoutState(productInfo, locked) {
    productInfo.querySelectorAll('shopify-payment-button').forEach((button) => {
      button.toggleAttribute('inert', locked);
      button.style.pointerEvents = locked ? 'none' : '';
      button.style.opacity = locked ? '0.5' : '';
      button.setAttribute('aria-disabled', locked ? 'true' : 'false');
    });
  }

  function setSizeError(productInfo, message = false) {
    const productForm = productInfo.querySelector('product-form');
    if (!productForm) return;

    if (typeof productForm.handleErrorMessage === 'function') {
      productForm.handleErrorMessage(message);
      return;
    }

    const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
    const messageElement = wrapper?.querySelector('.product-form__error-message');
    wrapper?.toggleAttribute('hidden', !message);
    if (message && messageElement) messageElement.textContent = message;
  }

  function lockPurchase(productInfo, showError = false) {
    if (!requiresSize(productInfo) || (hasSelectedSize(productInfo) && productInfo.dataset.wcfFitPending !== 'true')) {
      setDynamicCheckoutState(productInfo, false);
      return;
    }

    setDynamicCheckoutState(productInfo, true);
    if (showError) setSizeError(productInfo, MESSAGE);
  }

  function clearInitialSizeSelection(productInfo) {
    // A variant deep link chooses the color, never the customer's size.
    if (initializedProducts.has(productInfo) || !requiresSize(productInfo)) return;
    initializedProducts.add(productInfo);
    sizeSelects(productInfo).forEach((select) => {
      [...select.options].forEach((option) => option.removeAttribute('selected'));
      select.selectedIndex = -1;
    });

    sizeInputs(productInfo).forEach((input) => {
      input.checked = false;
      input.removeAttribute('checked');
    });
    productInfo
      .querySelectorAll('form[action*="/cart/add"] input[name="id"]')
      .forEach((input) => {
        input.value = '';
      });

    lockPurchase(productInfo);
  }

  function initialize() {
    document.querySelectorAll('product-info').forEach(clearInitialSizeSelection);

    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        const sizeRadio = target instanceof HTMLInputElement && isSizeInput(target);
        const sizeDropdown = target instanceof HTMLSelectElement && [...target.options].some(isSizeInput);
        if (!sizeRadio && !sizeDropdown) return;

        const productInfo = target.closest('product-info');
        if (!productInfo || !requiresSize(productInfo)) return;

        if (!hasSelectedSize(productInfo)) {
          event.stopImmediatePropagation();
          lockPurchase(productInfo, true);
          return;
        }

        setSizeError(productInfo);
        setDynamicCheckoutState(productInfo, false);
        scheduleVariantIdSync(productInfo);
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest(
          '[id^="ProductSubmitButton-"], button[type="submit"][name="add"]'
        );
        if (!button) return;

        const productInfo = button.closest('product-info');
        if (!productInfo || !requiresSize(productInfo) || hasSelectedSize(productInfo)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        lockPurchase(productInfo, true);
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        const productInfo = form.closest('product-info');
        if (!productInfo || !requiresSize(productInfo) || hasSelectedSize(productInfo)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        lockPurchase(productInfo, true);
      },
      true
    );

    new MutationObserver(() => {
      document.querySelectorAll('product-info').forEach((productInfo) => {
        clearInitialSizeSelection(productInfo);
        lockPurchase(productInfo);
        scheduleVariantIdSync(productInfo);
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();